import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EndUserLicenseAgreementItem } from '../models/end-user-license-agreement.model';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { NotificationService } from '../../../../../common';
import { EndUserLicenseAgreementItemService } from './end-user-license-agreement-item.service';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { EndUserLicenseAgreementListFacade } from '../list/end-user-license-agreement-list.facade';
import {
  EndUserLicenseAgreementItemInitialState,
  EndUserLicenseAgreementItemLoad,
  EndUserLicenseAgreementItemPublish,
  EndUserLicenseAgreementItemSave,
} from './end-user-license-agreement-item.actions';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface EndUserLicenseAgreementItemStateModel extends EndUserLicenseAgreementItem {
  loaded: boolean;
}

const initialState: EndUserLicenseAgreementItemStateModel = {
  ...new EndUserLicenseAgreementItem(),
  loaded: false,
};

@State<EndUserLicenseAgreementItemStateModel>({
  name: 'endUserLicenseAgreementItem',
  defaults: initialState,
})
@Injectable()
export class EndUserLicenseAgreementItemState {
  @Selector()
  static getState(state: EndUserLicenseAgreementItemStateModel): EndUserLicenseAgreementItemStateModel {
    return state;
  }

  @Selector()
  static getEulaItemId(state: EndUserLicenseAgreementItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getLoaded(state: EndUserLicenseAgreementItemStateModel): boolean {
    return state.loaded;
  }

  constructor(
    private eulaItemService: EndUserLicenseAgreementItemService,
    private navigateUserService: NavigateUserService,
    private notificationService: NotificationService,
    private eulaListFacade: EndUserLicenseAgreementListFacade,
  ) {}

  @Action(EndUserLicenseAgreementItemLoad)
  getEulaItem(
    ctx: StateContext<EndUserLicenseAgreementItemStateModel>,
    { payload }: EndUserLicenseAgreementItemLoad,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    return this.eulaItemService.getEulaItem(payload).pipe(
      map((eulaItem: EndUserLicenseAgreementItem) => {
        ctx.patchState({ ...eulaItem, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(EndUserLicenseAgreementItemSave)
  saveEulaItem(
    ctx: StateContext<EndUserLicenseAgreementItemStateModel>,
    { payload }: EndUserLicenseAgreementItemSave,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    return this.eulaItemService.saveEulaItem(payload).pipe(
      map(() => {
        this.notificationService.success('api.eula.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/end-user-license-agreement');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(EndUserLicenseAgreementItemPublish)
  publishEulaItem(
    ctx: StateContext<EndUserLicenseAgreementItemStateModel>,
    { payload }: EndUserLicenseAgreementItemPublish,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    return this.eulaItemService.publishEulaItem(payload.eulaItemId).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.eula.success.publish');
        this.eulaListFacade.loadAll();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(EndUserLicenseAgreementItemInitialState)
  setInitialState(ctx: StateContext<EndUserLicenseAgreementItemStateModel>): void {
    ctx.setState(initialState);
  }
}
